import { useRef } from "react";

import type { Box, Layer } from "./scene";

/**
 * Drag and resize what is on the frame, on top of the preview.
 *
 * ## Nothing here is ever drawn into the canvas
 *
 * That canvas is what `captureStream()` is pulling from - the preview *is* the
 * broadcast. A selection outline painted into it would be a selection outline
 * on every listener's screen, and the handles would go out live too. So this is
 * DOM sitting over the canvas, in the same box, and the two never touch.
 *
 * It is also why the overlay has to match the canvas exactly rather than
 * approximately: the boxes it draws are the boxes the compositor paints, and
 * any difference between them is a host positioning something by eye against a
 * lie.
 *
 * ## Everything is normalised
 *
 * Pointer movement is converted to fractions of the frame before it reaches a
 * layer, so a drag means the same thing on a 380px preview as on the 1280px
 * canvas. The alternative - working in preview pixels and scaling on commit -
 * puts a rounding error between what the host sees and what goes out, and the
 * error changes when they resize the column.
 */

/** The smallest a layer may be dragged, as a fraction of the frame. */
const MIN = 0.05;

type Grip = "move" | "nw" | "ne" | "sw" | "se" | "n" | "s" | "e" | "w";

export function SceneEditor({
  layers,
  selected,
  empty,
  onSelect,
  onChange,
  onRemove,
}: {
  layers: Layer[];
  selected: string | null;
  /**
   * Layer kinds with nothing to draw at this moment.
   *
   * A now-playing card with no track playing, or a camera that has not been
   * switched on, paints nothing at all - so without this the host is asked to
   * position a rectangle they cannot see. The placeholder cannot go in the
   * canvas, because the canvas is the broadcast, so it goes here.
   */
  empty: ReadonlySet<Layer["kind"]>;
  onSelect: (id: string | null) => void;
  onChange: (id: string, box: Box) => void;
  onRemove: (id: string) => void;
}) {
  const frame = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{
    grip: Grip;
    id: string;
    start: Box;
    x: number;
    y: number;
  } | null>(null);

  const begin = (
    event: React.PointerEvent,
    layer: Layer,
    grip: Grip,
  ): void => {
    // Or the click reaches the backdrop underneath and immediately deselects.
    event.stopPropagation();
    event.preventDefault();

    onSelect(layer.id);

    drag.current = {
      grip,
      id: layer.id,
      start: { ...layer.box },
      x: event.clientX,
      y: event.clientY,
    };

    (event.target as HTMLElement).setPointerCapture(event.pointerId);
  };

  const move = (event: React.PointerEvent): void => {
    const state = drag.current;
    const rect = frame.current?.getBoundingClientRect();

    if (!state || !rect || rect.width === 0 || rect.height === 0) return;

    const dx = (event.clientX - state.x) / rect.width;
    const dy = (event.clientY - state.y) / rect.height;

    onChange(state.id, resize(state.start, state.grip, dx, dy));
  };

  const end = (event: React.PointerEvent): void => {
    drag.current = null;
    (event.target as HTMLElement).releasePointerCapture(event.pointerId);
  };

  /*
   * Arrow keys, because a logo three pixels out of line is a real thing to want
   * fixed and dragging cannot do it. Shift moves in bigger steps; Delete takes
   * the layer off the frame.
   */
  const key = (event: React.KeyboardEvent): void => {
    if (!selected) return;

    const layer = layers.find((l) => l.id === selected);

    if (!layer) return;

    if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault();
      onRemove(selected);

      return;
    }

    const step = event.shiftKey ? 0.05 : 0.005;
    const nudge: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };

    const delta = nudge[event.key];

    if (!delta) return;

    event.preventDefault();
    onChange(selected, resize(layer.box, "move", delta[0], delta[1]));
  };

  return (
    <div
      ref={frame}
      tabIndex={0}
      role="application"
      aria-label="Arrange the frame"
      onKeyDown={key}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={end}
      // Clicking empty space deselects, which is how every canvas editor
      // behaves and the only way to get the handles out of the way.
      onPointerDown={() => onSelect(null)}
      className="absolute inset-0 touch-none outline-none"
      data-scene-editor
    >
      {layers.map((layer) => {
        if (!layer.visible) return null;

        const active = layer.id === selected;
        const blank = empty.has(layer.kind);

        return (
          <div
            key={layer.id}
            data-scene-layer={layer.id}
            data-selected={active || undefined}
            data-scene-empty={blank || undefined}
            onPointerDown={(event) => begin(event, layer, "move")}
            style={{
              left: `${layer.box.x * 100}%`,
              top: `${layer.box.y * 100}%`,
              width: `${layer.box.w * 100}%`,
              height: `${layer.box.h * 100}%`,
            }}
            /*
             * Every layer is outlined, not only the selected one.
             *
             * The outline is faint because it sits over a picture the host is
             * judging, and permanent because the alternative was a host hunting
             * for a rectangle by dragging at random. Dashed when there is
             * nothing inside it to see.
             */
            className={`group absolute cursor-move transition-colors ${
              active
                ? "border-2 border-accent"
                : blank
                  ? "border border-dashed border-ink/35 hover:border-accent/60"
                  : "border border-ink/15 hover:border-accent/60"
            }`}
          >
            {/*
              The name, so two overlapping rectangles are tellable apart. Always
              on for a layer with nothing in it - that outline is all there is -
              and on hover otherwise, where the picture is the thing worth
              looking at.
            */}
            <span
              className={`pointer-events-none absolute left-0 top-0 max-w-full truncate rounded-br-[3px] bg-app/85 px-1 py-px font-mono text-[9px] leading-tight transition-opacity ${
                active ? "text-ink" : "text-soft"
              } ${
                blank || active
                  ? "opacity-100"
                  : "opacity-0 group-hover:opacity-100"
              }`}
            >
              {NAMES[layer.kind]}
              {blank ? " · empty" : ""}
            </span>

            {active &&
              (["nw", "n", "ne", "e", "se", "s", "sw", "w"] as const).map(
                (grip) => (
                  <span
                    key={grip}
                    onPointerDown={(event) => begin(event, layer, grip)}
                    data-scene-grip={grip}
                    style={GRIPS[grip]}
                    /*
                     * Eight pixels, offset by four, so the handle straddles the
                     * edge it moves rather than sitting inside it. A handle
                     * wholly inside the box is one that cannot be grabbed when
                     * the box is small, which is exactly when it is needed.
                     */
                    className={`absolute h-2 w-2 rounded-[1px] border border-app bg-accent ${CURSORS[grip]}`}
                  />
                ),
              )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * A box after a drag, clamped so it stays usable.
 *
 * ## Why it stays on the frame
 *
 * Every edge is kept inside the frame, so a layer cannot be dragged off screen.
 * That is a deliberate restriction rather than an oversight - broadcast tools
 * generally allow bleeding off the edge, and the cost of allowing it here is a
 * host who loses a logo somewhere outside a preview they cannot scroll, with no
 * handle left to drag it back by.
 *
 * ## Why resizing cannot invert
 *
 * Dragging a left edge past the right one would produce a negative width, which
 * `drawImage` renders as nothing at all. Each edge stops `MIN` short of its
 * opposite instead, so the box gets small and stays visible.
 */
function resize(start: Box, grip: Grip, dx: number, dy: number): Box {
  if (grip === "move") {
    return {
      ...start,
      x: clamp(start.x + dx, 0, 1 - start.w),
      y: clamp(start.y + dy, 0, 1 - start.h),
    };
  }

  const box = { ...start };

  if (grip.includes("w")) {
    const x = clamp(start.x + dx, 0, start.x + start.w - MIN);

    box.w = start.x + start.w - x;
    box.x = x;
  }

  if (grip.includes("e")) {
    box.w = clamp(start.w + dx, MIN, 1 - start.x);
  }

  if (grip.includes("n")) {
    const y = clamp(start.y + dy, 0, start.y + start.h - MIN);

    box.h = start.y + start.h - y;
    box.y = y;
  }

  if (grip.includes("s")) {
    box.h = clamp(start.h + dy, MIN, 1 - start.y);
  }

  return box;
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

/**
 * What each layer is called on the frame.
 *
 * The host's words, not the code's: they chose "Now playing" in the panel, so
 * that is what the box over it says.
 */
const NAMES: Record<Layer["kind"], string> = {
  camera: "Camera",
  card: "Now playing",
  brand: "Your name",
  image: "Image",
};

/** Where each handle sits on its box, straddling the edge it moves. */
const GRIPS: Record<Exclude<Grip, "move">, React.CSSProperties> = {
  nw: { left: -4, top: -4 },
  n: { left: "calc(50% - 4px)", top: -4 },
  ne: { right: -4, top: -4 },
  e: { right: -4, top: "calc(50% - 4px)" },
  se: { right: -4, bottom: -4 },
  s: { left: "calc(50% - 4px)", bottom: -4 },
  sw: { left: -4, bottom: -4 },
  w: { left: -4, top: "calc(50% - 4px)" },
};

const CURSORS: Record<Exclude<Grip, "move">, string> = {
  nw: "cursor-nwse-resize",
  n: "cursor-ns-resize",
  ne: "cursor-nesw-resize",
  e: "cursor-ew-resize",
  se: "cursor-nwse-resize",
  s: "cursor-ns-resize",
  sw: "cursor-nesw-resize",
  w: "cursor-ew-resize",
};
